/* What a page needs before it can draw anything: who is signed in, the collections they can
   read, which one is active, and its spots. Nobody signed in means no page: the visitor is
   sent to the account page and comes back afterwards. That is a courtesy, not the guard —
   row-level security is, and a signed-out request gets no rows whatever the client does. */

import {
  AuthUnreachable,
  browserStore,
  createAuthApi,
  createCollectionsApi,
  createModerationApi,
  createProfilesApi,
  supabaseSource,
  type AuthUser,
  type Collection,
  type CollectionsApi,
  type KV,
  type LoadedSpots,
  type Profile,
} from "@preflop/api";
import type { Spot } from "@preflop/core";
import type { Topbar } from "../ui/topbar";
import { ACCOUNT_PAGE, supabase } from "./client";
import { applySeatNames } from "./seat-names";
import { forgetAccountSettings, loadSetting, reconcileSettings, saveSetting } from "./settings";
import { themes } from "./theme";

export interface Session {
  user: AuthUser;
  /** Null when the profile could not be read: offline, on a device that is signed in. */
  profile: Profile | null;
  /** The collections this user can make active: the base first, then their own. */
  collections: Collection[];
  /** The active one: what the viewer shows and the trainer drills. */
  collection: Collection;
  /** The collection's spots as the database holds them, before any local edit. */
  spots: Spot[];
  from: LoadedSpots["from"];
  /** A switch made on this device that the profile has not heard of yet. */
  switchPending: boolean;
  /** Whether the user manages the base ranges. False when it could not be asked: what an
      administrator may do is the database's to allow, this only decides what is offered. */
  isAdmin: boolean;
  api: CollectionsApi;
}

const CACHE_OWNER = "user";

/* The cache answers when the network does not, so it must only ever answer the user who
   filled it: a different user on this device starts from an empty one. */
export async function claimCache(cache: KV, userId: string): Promise<void> {
  if ((await cache.get<string>(CACHE_OWNER)) === userId) return;
  await clearCollectionCache(cache);
  await cache.set(CACHE_OWNER, userId);
}

/** Forget what this device holds of an account: every cached collection, which one was
    active, and the settings that follow the account. Called on sign-out, and when a different
    user turns up. Device settings (edit mode) stay: they are about the device. */
export async function clearCollectionCache(cache: KV = browserStore("collections")): Promise<void> {
  for (const key of await cache.keys()) await cache.delete(key);
  await saveSetting(ACTIVE_SETTING, null);
  await forgetAccountSettings();
  await applySeatNames(); // none left: the next account does not inherit this one's names
  themes.forget();
}

/** Leave for the account page, remembering where to come back to. Never resolves: the page is
    on its way out, and nothing after the call should run. */
export function toAccountPage(): Promise<never> {
  const next = location.pathname + location.search + location.hash;
  location.replace(`${ACCOUNT_PAGE}?next=${encodeURIComponent(next)}`);
  return new Promise(() => {});
}

/* ---- the active collection ---- */

/**
 * The collections a user can make active from the switcher: the base and their own, and the
 * one public collection of someone else's they chose in the gallery, `keep`, for as long as it
 * is the active one. Row-level security lets them read every public collection, but a
 * stranger's turning up in the switcher unasked would be just that: the gallery is where
 * those are found.
 */
export const switchable = (
  list: readonly Collection[],
  userId: string,
  keep?: string | null,
): Collection[] =>
  list.filter(
    (c) => c.isBase || c.ownerId === userId || (c.id === keep && c.visibility === "public"),
  );

/** The collection that would be active, by its id alone: what `pickActive` looks for. */
const wantedId = (
  profile: Pick<Profile, "activeCollectionId"> | null,
  device: DeviceChoice | null,
): string | null =>
  (device && (!profile || !device.synced) ? device.id : profile?.activeCollectionId) ?? null;

/** The choice as this device remembers it. `id` null is the base, whatever its id is. */
export interface DeviceChoice {
  id: string | null;
  /** False when the profile could not be told: this device's choice stands until it is. */
  synced: boolean;
}
export const ACTIVE_SETTING = "activeCollection";

const readChoice = (raw: unknown): DeviceChoice | null => {
  if (!raw || typeof raw !== "object") return null;
  const { id, synced } = raw as Record<string, unknown>;
  return id === null || typeof id === "string" ? { id, synced: synced === true } : null;
};

/**
 * Which collection a page opens on. The profile decides, since it is what the user's other
 * devices see; this device's memory decides when the profile cannot be read (offline) or has
 * not heard of the last switch yet. A choice that is no longer in the list — deleted, or made
 * private again by its owner — falls back to the base.
 */
export function pickActive(
  collections: readonly Collection[],
  profile: Pick<Profile, "activeCollectionId"> | null,
  device: DeviceChoice | null,
): Collection | undefined {
  const wanted = wantedId(profile, device);
  return (
    (wanted ? collections.find((c) => c.id === wanted) : undefined) ??
    collections.find((c) => c.isBase) ??
    collections[0]
  );
}

/** Make a collection the active one: on this device at once, on the profile if it can be
    reached. Resolves to whether the profile was told. */
export async function switchCollection(
  userId: string,
  to: Pick<Collection, "id" | "isBase">,
): Promise<boolean> {
  const id = to.isBase ? null : to.id;
  await saveSetting(ACTIVE_SETTING, { id, synced: false } satisfies DeviceChoice);
  try {
    await createProfilesApi(supabase()).updateProfile(userId, { activeCollectionId: id });
  } catch {
    return false;
  }
  await saveSetting(ACTIVE_SETTING, { id, synced: true } satisfies DeviceChoice);
  return true;
}

/** Put the switcher in a page's top bar. Switching reloads: both pages build everything from
    `openSession()`, and the spots come from the cache, so it costs nothing to be thorough. */
export function wireSwitcher(bar: Topbar, s: Session, canLeave: () => boolean = () => true): void {
  bar.setCollections(
    s.collections,
    s.collection.id,
    (c) => c.ownerId === s.user.id,
    async (next) => {
      if (!canLeave()) return false;
      await switchCollection(s.user.id, next);
      location.reload();
      return true;
    },
  );
}

/* ---- opening ---- */

export async function openSession(): Promise<Session> {
  const db = supabase();
  const cache = browserStore("collections");
  const user = await createAuthApi(db)
    .currentUser()
    .catch(async (e: unknown) => {
      // Offline past the life of the stored token. Whoever filled this device's cache was
      // signed in here, and the cache is all that can answer now: carry on as them.
      const id = e instanceof AuthUnreachable ? await cache.get<string>(CACHE_OWNER) : null;
      if (!id) throw e;
      return { id, email: "" } satisfies AuthUser;
    });
  if (!user) return toAccountPage();

  await claimCache(cache, user.id);
  const api = createCollectionsApi(supabaseSource(db), cache);
  const [listed, isAdmin, profile, device] = await Promise.all([
    api.listCollections(),
    createProfilesApi(db)
      .isAdmin()
      .catch(() => false),
    // The pages draw without it; the account link falls back to the email.
    createProfilesApi(db)
      .getProfile(user.id)
      .catch(() => null),
    loadSetting<unknown>(ACTIVE_SETTING, null).then(readChoice),
  ]);
  // The account's theme, unless this device holds a choice it never managed to push.
  if (profile)
    await themes
      .reconcile(profile.theme, async (theme) => {
        await createProfilesApi(db).updateProfile(user.id, { theme });
      })
      .catch(() => undefined);

  // The settings that follow the account are brought into line before any page reads one.
  // Without a profile (offline) the device's own stand, and are pushed the next time.
  if (profile)
    await reconcileSettings(profile.settings, async (settings) => {
      await createProfilesApi(db).updateProfile(user.id, { settings });
    }).catch(() => undefined);
  // What this user calls the seats, before anything names one.
  await applySeatNames();

  // Someone else's public collection stays in the list for as long as it is the one chosen.
  const collections = switchable(listed, user.id, wantedId(profile, device));
  const collection = pickActive(collections, profile, device);
  if (!collection)
    throw new Error("There is no collection to read yet. Put the base in with `pnpm hydrate`.");

  // A switch the profile never heard of: tell it now, and do not wait for the answer.
  const switchPending = !!device && !device.synced;
  if (switchPending && profile) void switchCollection(user.id, collection);

  // Timed, for the load check (e2e/load.spec.ts) and for anyone with the devtools open: how
  // long the active collection took to be ready, and whether it came from the cache.
  const asked = performance.now();
  const { spots, from } = await api.loadSpots(collection);
  performance.measure("preflop:load-spots", {
    start: asked,
    detail: { from, spots: spots.length },
  });
  return { user, profile, collections, collection, spots, from, switchPending, isAdmin, api };
}

/** The status line for the top bar. The collection's name is in the switcher beside it. */
export function describeSession(s: Session): string {
  const parts = [`${s.spots.length} ${s.spots.length === 1 ? "chart" : "charts"}`];
  if (s.from === "stale") parts.push("offline copy");
  if (s.switchPending) parts.push("switch not saved to your account yet");
  return parts.join(" · ");
}

/** Who is signed in, in a page's top bar; and for an administrator the way to the moderation
    page, with how many collections have reports waiting. The count is an extra, asked for
    after the page is up and never waited for. */
export function showAccount(bar: Topbar, s: Pick<Session, "user" | "profile" | "isAdmin">): void {
  bar.setAccount(accountLabel(s));
  if (!s.isAdmin) return;
  bar.setModeration(0);
  void createModerationApi(supabase())
    .queue()
    .then((cases) => bar.setModeration(cases.filter((c) => c.waiting > 0).length))
    .catch(() => undefined);
}

/** What the account link in the top bar says. */
export const accountLabel = (s: Pick<Session, "user" | "profile">): string =>
  s.profile?.displayName || s.user.email || "Account";
